import React, { useEffect, useState } from "react"
import Typography from "aldoo-ra/Typography"
import Button from "aldoo-ra/Button"
import Tabs from "aldoo-ra/Tabs"
import { showNotification } from "aldoo-ra/Notification"
import { GetParams, GetParam, CreateParam, UpdateParam } from "../../ParamIO/api"
import { CreateAdsAccessKey, ListAccessKeys, ConnectAccount, ListAccounts, AccountCRUD } from "../api"

// ParamIO addresses for the aldoo-na/aldoo-ads module config. The framework's
// param-io boot sync mirrors module config keys to FLAT addresses (author
// LocalConfig) and preserves admin edits via config hashes — we edit those.
const ADDRESSES = ["ADS_SERVING_ENABLED", "ADS_ATTRIBUTION_TOUCH", "ADS_IMPRESSION_DEDUP_MINUTES", "ADS_UTM_MEDIUM"]
const DEFAULTS = {
  ADS_SERVING_ENABLED: true,
  ADS_ATTRIBUTION_TOUCH: "last",
  ADS_IMPRESSION_DEDUP_MINUTES: "30",
  ADS_UTM_MEDIUM: "aldoo-ads",
}
const toBool = (v) => v === true || v === "true"

const TOUCH_OPTIONS = [
  { value: "first", label: "First touch — credit the ad that ACQUIRED the user" },
  { value: "last", label: "Last touch — credit the ad that CONVERTED the user" },
  { value: "both", label: "Both — report first and last as separate columns" },
]

// ── General tab — plain controlled form over ParamIO ────────────────────────
// Deliberately NOT the CMS ContentEditor: its autosave fires partial value
// maps during the on-mount onChange cascade, which the api() sanitizer
// strips to value-less update_param calls (param-io: missing_value). A
// single explicit Save with controlled inputs can't post an undefined value.
const GeneralTab = () => {
  const [v, setV] = useState(DEFAULTS)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    ;(async () => {
      const next = { ...DEFAULTS }
      for (const address of ADDRESSES) {
        const param = await GetParams({ match: { address } }).catch(() => null)
        const raw = param?.data?.[0]?.value
        if (raw === undefined) continue
        next[address] = address === "ADS_SERVING_ENABLED" ? toBool(raw) : String(raw ?? "")
      }
      setV(next)
      setLoading(false)
    })()
  }, [])

  const set = (k) => (val) => setV((prev) => ({ ...prev, [k]: val }))

  const save = async () => {
    setSaving(true)
    try {
      const writes = ADDRESSES.map((address) => {
        let value = v[address]
        if (address === "ADS_SERVING_ENABLED") value = toBool(value)
        else if (address === "ADS_IMPRESSION_DEDUP_MINUTES") value = parseInt(value, 10) || 0
        else value = String(value ?? "") // never undefined
        return GetParam(address)
          .then((existing) =>
            existing && existing.result !== false ? UpdateParam({ address, value }) : CreateParam({ address, value })
          )
          .catch(() => CreateParam({ address, value }))
      })
      await Promise.all(writes)
      showNotification({ message: "Ads settings saved", className: "bg-admin_success text-admin_text_inv" })
    } catch {
      showNotification({ message: "Failed to save ads settings", className: "bg-admin_error text-admin_text_inv" })
    } finally {
      setSaving(false)
    }
  }

  if (loading) return <Typography variant="admin_body1">Loading…</Typography>

  const fieldRow = (label, desc, control) => (
    <div className="flex flex-col gap-1 py-3 border-b border-admin_bordered last:border-0">
      <span className="text-sm font-semibold text-admin_text">{label}</span>
      {desc && <span className="text-xs text-admin_text_secondary">{desc}</span>}
      <div className="mt-1">{control}</div>
    </div>
  )

  return (
    <div className="bg-paper rounded-lg border border-admin_bordered p-5 max-w-3xl flex flex-col gap-1">
      {fieldRow(
        "Serving Enabled",
        "Master switch. When off, the serve endpoint returns nothing and every <AldooAd/> slot renders empty on all sites.",
        <label className="inline-flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={toBool(v.ADS_SERVING_ENABLED)}
            onChange={(e) => set("ADS_SERVING_ENABLED")(e.target.checked)} />
          <span className="text-sm">{toBool(v.ADS_SERVING_ENABLED) ? "On" : "Off"}</span>
        </label>
      )}

      {fieldRow(
        "Attribution Touch",
        "Which touch credits signups & revenue to an ad. First = the campaign that acquired the user (their first ever campaign visit). Last = the campaign that converted them (most recent visit before signup/purchase). Both = the dashboard shows two columns to compare acquisition vs conversion.",
        <select className="border border-admin_bordered rounded-lg px-3 py-2 bg-transparent w-full"
          value={v.ADS_ATTRIBUTION_TOUCH} onChange={(e) => set("ADS_ATTRIBUTION_TOUCH")(e.target.value)}>
          {TOUCH_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      )}

      {fieldRow(
        "Impression Dedup (minutes)",
        "Repeat impressions from the same visitor for the same ad within this window count once. 0 disables deduplication.",
        <input type="number" min="0" className="border border-admin_bordered rounded-lg px-3 py-2 bg-transparent w-40"
          value={v.ADS_IMPRESSION_DEDUP_MINUTES} onChange={(e) => set("ADS_IMPRESSION_DEDUP_MINUTES")(e.target.value)} />
      )}

      {fieldRow(
        "Click UTM Medium",
        "utm_medium stamped on every ad click redirect (default: aldoo-ads).",
        <input className="border border-admin_bordered rounded-lg px-3 py-2 bg-transparent w-full"
          value={v.ADS_UTM_MEDIUM} onChange={(e) => set("ADS_UTM_MEDIUM")(e.target.value)} />
      )}

      <div className="pt-4">
        <Button variant="admin_primary" onClick={save} disabled={saving}>
          {saving ? "Saving…" : "Save settings"}
        </Button>
      </div>
    </div>
  )
}

// ── Access Keys tab — mint ads:read keys for partner sites ─────────────────
const AccessKeysTab = () => {
  const [keys, setKeys] = useState([])
  const [freshKey, setFreshKey] = useState(null)

  const load = async () => setKeys(await ListAccessKeys().catch(() => []))
  useEffect(() => { load() }, [])

  const create = async () => {
    const res = await CreateAdsAccessKey("Aldoo Ads access key (ads:read)")
    if (!res?.result) {
      showNotification({ message: "Failed to create key", className: "bg-admin_error text-admin_text_inv" })
      return
    }
    setFreshKey(res.key)
    load()
  }

  return (
    <div className="flex flex-col gap-4">
      <Typography variant="admin_body1">
        Generate an access key with the <b>ads:read</b> permission and give it to a partner
        site — they paste it in their Connect Account tab (together with this site's API URL)
        to list this account's campaigns, render its ads and read its stats.
      </Typography>
      <div>
        <Button variant="admin_primary" onClick={create}>Create Access Key</Button>
      </div>
      {freshKey && (
        <div className="border border-admin_success rounded-lg p-4 bg-admin_success/10">
          <Typography variant="admin_body1">
            Key created — copy it now (shown once): <code className="font-mono select-all">{freshKey}</code>
          </Typography>
        </div>
      )}
      <div className="flex flex-col gap-2">
        {keys.map((k) => (
          <div key={k._id} className="border border-admin_bordered rounded-lg p-3 flex justify-between text-sm">
            <span className="font-mono">{k.key ? `${String(k.key).slice(0, 8)}…` : k._id}</span>
            <span className="text-admin_text_secondary">{k.status}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

// ── Connect Account tab — link a remote install ─────────────────────────────
const ConnectAccountTab = () => {
  const [apiUrl, setApiUrl] = useState("")
  const [accessKey, setAccessKey] = useState("")
  const [name, setName] = useState("")
  const [busy, setBusy] = useState(false)
  const [accounts, setAccounts] = useState([])

  const load = async () => {
    const res = await ListAccounts().catch(() => null)
    setAccounts((res?.accounts || []).filter((a) => a.type === "remote"))
  }
  useEffect(() => { load() }, [])

  const connect = async () => {
    if (!apiUrl.trim() || !accessKey.trim()) return
    setBusy(true)
    try {
      const res = await ConnectAccount({ api_url: apiUrl.trim(), access_key: accessKey.trim(), name: name.trim() })
      if (res?.result) {
        showNotification({ message: "Account connected", className: "bg-admin_success text-admin_text_inv" })
        setApiUrl(""); setAccessKey(""); setName("")
        load()
      } else {
        showNotification({
          message: res?.error === "remote_unreachable" ? "Remote API unreachable"
            : res?.error === "remote_rejected" ? "Remote rejected the key (needs ads:read)"
            : "Failed to connect account",
          className: "bg-admin_error text-admin_text_inv",
        })
      }
    } finally {
      setBusy(false)
    }
  }

  const remove = async (a) => {
    await AccountCRUD.SoftDelete({ _id: a._id })
    load()
  }

  return (
    <div className="flex flex-col gap-4 max-w-2xl">
      <Typography variant="admin_body1">
        Link another aldoo site's ad account ("Link Aldoo Ad Account"): paste its API URL and
        the access key generated on that site (Settings → Access Keys). Once connected, its
        campaigns and stats appear in this dashboard and its ads can be served here with
        {" "}<code>{`<AldooAd id account/>`}</code>.
      </Typography>
      <input className="border border-admin_bordered rounded-lg px-3 py-2 bg-transparent"
        placeholder="API URL — e.g. https://api-transfer.aldoo.com"
        value={apiUrl} onChange={(e) => setApiUrl(e.target.value)} />
      <input className="border border-admin_bordered rounded-lg px-3 py-2 bg-transparent font-mono"
        placeholder="Access key (ads:read)"
        value={accessKey} onChange={(e) => setAccessKey(e.target.value)} />
      <input className="border border-admin_bordered rounded-lg px-3 py-2 bg-transparent"
        placeholder="Display name (optional)"
        value={name} onChange={(e) => setName(e.target.value)} />
      <div>
        <Button variant="admin_primary" onClick={connect} disabled={busy}>
          {busy ? "Connecting…" : "Connect Account"}
        </Button>
      </div>

      {accounts.length > 0 && (
        <div className="flex flex-col gap-2 pt-2">
          <Typography variant="admin_h4">Connected accounts</Typography>
          {accounts.map((a) => (
            <div key={a._id} className="border border-admin_bordered rounded-lg p-3 flex justify-between items-center text-sm">
              <span>{a.name} — <span className="text-admin_text_secondary">{a.api_url}</span></span>
              <Button variant="admin_secondary" onClick={() => remove(a)}>Remove</Button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ── Settings shell ──────────────────────────────────────────────────────────
const Settings = () => {
  const [tab, setTab] = useState("general")
  const tabs = [
    { value: "general", label: "General" },
    { value: "keys", label: "Access Keys" },
    { value: "connect", label: "Connect Account" },
  ]
  return (
    <div className="p-6 flex flex-col gap-5">
      <Typography variant="admin_h3">Aldoo Ads Settings</Typography>
      <Tabs variant="admin" tabs={tabs} value={tab} onChange={setTab} />
      {tab === "general" && <GeneralTab />}
      {tab === "keys" && <AccessKeysTab />}
      {tab === "connect" && <ConnectAccountTab />}
    </div>
  )
}

export default Settings
